import {
  planningGroups,
  plansIn,
  mutablePlans,
  savePlans,
  replacePlans,
  planTarget,
  preservePlanHistory,
} from "../core/planning.js";
import { l, t } from "../core/i18n.js";
import { esc, groupLabel, validExamDate } from "../core/model.js";
import { toast } from "../core/navigation.js";
import { saveJSON } from "../core/storage.js";
import { KEYS } from "../../shared/schema.js";
import { render } from "./views.js";
import { runtime } from "../core/runtime.js";
import { offerUndo } from "./undo.js";

export function openPlanModal(
  task = null,
  day = runtime.page === "manage"
    ? runtime.manageDay
    : planTarget(runtime.selectedDate),
) {
  const isEdit = !!task,
    first = !plansIn(day).length;
  const group = planningGroups().find((item) => item.id === day);
  const scope = day.includes(":")
    ? t("Only on {date}", { date: groupLabel(day) })
    : t("Repeats on {days}", { days: group?.detail || groupLabel(day) });
  const host = document.getElementById("modal");
  host.innerHTML = `<div class="modal" role="dialog" aria-modal="true"><h2>${t(isEdit ? "Edit plan" : "Add plan")}</h2><p class="modal-scope">${esc(scope)}</p><form data-plan-form><label for="mName">${t("Plan name")}</label><input id="mName" class="field" required maxlength="160" value="${esc(task?.name || "")}" placeholder="${t("What would you like to do?")}"><label for="mSchedule">${t("Time / schedule")}</label><input id="mSchedule" class="field" maxlength="120" value="${esc(task?.schedule || "")}" placeholder="${t("Optional")}"><label for="mNotes">${t("Notes")}</label><textarea id="mNotes" class="field" rows="3" maxlength="10000">${esc(task?.notes || "")}</textarea><label class="modal-study"><input id="mStudy" type="checkbox" ${task?.study ? "checked" : ""}>${t("Highlight as a study task in the table")}</label><div class="modal-actions"><button class="btn" type="button" data-close>${t("Cancel")}</button><button class="btn black" type="submit" data-save-plan>${t(isEdit ? "Save changes" : first ? "Create plan" : "Add plan")}</button></div></form></div>`;
  host.classList.add("show");
  document.getElementById("mName").focus();
  host.querySelector("[data-plan-form]").onsubmit = (event) => {
    event.preventDefault();
    const name = document.getElementById("mName").value.trim();
    if (!name) return;
    preservePlanHistory();
    const changes = {
      name,
      schedule: document.getElementById("mSchedule").value.trim(),
      notes: document.getElementById("mNotes").value.trim(),
      study: document.getElementById("mStudy").checked,
    };
    if (isEdit)
      Object.assign(
        mutablePlans(day).find((item) => item.id === task.id),
        changes,
      );
    else
      mutablePlans(day).push({ id: "plan_" + crypto.randomUUID(), ...changes });
    savePlans(day);
    saveJSON(KEYS.custom, runtime.state.custom);
    closeModal();
    render();
    toast(t(isEdit ? "Plan updated." : "Plan added."));
  };
}

export function closeModal() {
  // Release the workspace before callers replace its children after saving.
  for (const selector of [".app", "#mobileNav", "#studyWidget"])
    document.querySelector(selector).inert = false;
  document.getElementById("modal").classList.remove("show");
  document.getElementById("modal").innerHTML = "";
  document.dispatchEvent(new Event("dayx:modal-closed"));
}

export function openExamModal(exam = null) {
  const isEdit = !!exam,
    modal = document.getElementById("modal");
  modal.innerHTML = `${l('<div class="modal exam-modal" role="dialog" aria-modal="true" aria-labelledby="examModalTitle"><div class="exam-modal-kicker">EXAM DATE</div><h2 id="examModalTitle">')}${isEdit ? l("Edit exam") : l("Add an exam")}${l('</h2><p>Give this date a name. The countdown updates automatically each day.</p><label for="examName">Exam name</label><input id="examName" class="field" maxlength="120" placeholder="For example: IELTS" value="')}${esc(exam?.name || "")}${l('"><label for="examDate">Exam date</label><input id="examDate" class="field" type="date" value="')}${esc(exam?.date || "")}${l('"><label for="examNotes">Notes <span>(optional)</span></label><textarea id="examNotes" class="field" rows="3" maxlength="300" placeholder="Location, target score, or anything to remember">')}${esc(exam?.notes || "")}${l('</textarea><div class="modal-actions"><button class="btn" type="button" data-close>Cancel</button><button class="btn black" type="button" data-save-exam>')}${isEdit ? l("Save changes") : l("Add exam")}</button></div></div>`;
  modal.classList.add("show");
  document.getElementById("examName").focus();
  const save = modal.querySelector("[data-save-exam]");
  save.onclick = () => {
    const name = document.getElementById("examName").value.trim(),
      date = document.getElementById("examDate").value,
      notes = document.getElementById("examNotes").value.trim();
    if (!name) {
      document.getElementById("examName").focus();
      toast("Enter an exam name.");
      return;
    }
    if (!validExamDate(date)) {
      document.getElementById("examDate").focus();
      toast("Choose a valid exam date.");
      return;
    }
    if (
      runtime.state.exams.some(
        (x) =>
          x.id !== exam?.id &&
          x.date === date &&
          String(x.name || "")
            .trim()
            .toLowerCase() === name.toLowerCase(),
      )
    ) {
      toast("This exam is already in your list.");
      return;
    }
    if (isEdit) Object.assign(exam, { name, date, notes });
    else
      runtime.state.exams.push({
        id: `exam_${Date.now()}_${Math.random().toString(36).slice(2)}`,
        name,
        date,
        notes,
      });
    saveJSON(KEYS.exams, runtime.state.exams);
    closeModal();
    render();
    toast(isEdit ? l("Exam updated.") : l("Exam added."));
  };
  modal.querySelectorAll("input").forEach((input) =>
    input.addEventListener("keydown", (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        save.click();
      }
    }),
  );
}

export function deleteExam(exam) {
  const index = runtime.state.exams.findIndex((x) => x.id === exam.id);
  if (index < 0) return;
  runtime.state.exams.splice(index, 1);
  saveJSON(KEYS.exams, runtime.state.exams);
  render();
  offerUndo(t("Exam deleted."), () => {
    if (runtime.state.exams.some((x) => x.id === exam.id)) return;
    runtime.state.exams.splice(
      Math.min(index, runtime.state.exams.length),
      0,
      exam,
    );
    saveJSON(KEYS.exams, runtime.state.exams);
    render();
    toast(t("Exam restored."));
  });
}

export function deletePlan(
  task,
  day = runtime.page === "manage"
    ? runtime.manageDay
    : planTarget(runtime.selectedDate),
) {
  preservePlanHistory();
  const plans = plansIn(day),
    index = plans.findIndex((item) => item.id === task.id);
  if (index < 0) return;
  replacePlans(
    day,
    plans.filter((item) => item.id !== task.id),
  );
  saveJSON(KEYS.custom, runtime.state.custom);
  render();
  offerUndo(t("Plan deleted."), () => {
    const current = mutablePlans(day);
    if (current.some((item) => item.id === task.id)) return;
    current.splice(Math.min(index, current.length), 0, task);
    savePlans(day);
    saveJSON(KEYS.custom, runtime.state.custom);
    render();
    toast(t("Plan restored."));
  });
}

// The original planner named these flows; deletion is now immediate with undo.
export { deletePlan as confirmDelete, deleteExam as confirmDeleteExam };
